import { useEffect, useState, type FormEvent } from 'react'
import { PageHeader, Alert, FormField, card, colors, btnPrimary } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { useSupportEmail } from '../../hooks/use-support-email'

interface OrganizationRow {
  readonly id: string
  readonly name: string
  readonly contactName: string | null
  readonly contactEmail: string | null
  readonly contactPhone: string | null
  readonly street: string | null
  readonly city: string | null
  readonly state: string | null
  readonly zip: string | null
  readonly country: string | null
  readonly cliaNumber: string | null
  readonly labDirector: string | null
  readonly npi: string | null
}

function readOnlyAddress(org: OrganizationRow): string {
  const parts = [org.street, [org.city, org.state].filter(Boolean).join(', '), org.zip, org.country].filter(Boolean)
  return parts.join(' · ') || '—'
}

// customer_admin self-service. Edit the organization's contact details;
// everything else (name, address, compliance) is read-only and routes the
// user to support to change.
export function MyOrganizationPage() {
  const supportEmail = useSupportEmail()
  const [org, setOrg] = useState<OrganizationRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ contactEmail: '', contactName: '', contactPhone: '' })

  async function refresh(): Promise<void> {
    setLoading(true)
    try {
      const res = await api.get<OrganizationRow>('/organizations/me')
      if (res.data) {
        setOrg(res.data)
        setForm({
          contactEmail: res.data.contactEmail ?? '',
          contactName: res.data.contactName ?? '',
          contactPhone: res.data.contactPhone ?? '',
        })
      }
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load organization')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  async function handleSave(e: FormEvent): Promise<void> {
    e.preventDefault()
    setSaving(true)
    setSaved(false)
    setError(null)
    try {
      const payload: Record<string, string> = {}
      if (form.contactEmail.trim()) payload.contactEmail = form.contactEmail.trim()
      if (form.contactName.trim()) payload.contactName = form.contactName.trim()
      if (form.contactPhone.trim()) payload.contactPhone = form.contactPhone.trim()
      await api.patch('/organizations/me', payload)
      setSaved(true)
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <PageHeader title="My Organization" subtitle="Update your contact details" />

      {error && <Alert type="error" message={error} onDismiss={() => setError(null)} />}

      {loading || !org ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <form onSubmit={handleSave} style={{ ...card, marginBottom: '1rem', maxWidth: '560px' }}>
            <h3 style={{ marginTop: 0 }}>Contact details</h3>
            {saved && <Alert type="success" message="Contact details updated." onDismiss={() => setSaved(false)} />}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <FormField
                name="contactEmail"
                label="Contact email"
                type="email"
                value={form.contactEmail}
                onChange={(v) => setForm((p) => ({ ...p, contactEmail: v }))}
              />
              <FormField
                name="contactName"
                label="Contact name"
                value={form.contactName}
                onChange={(v) => setForm((p) => ({ ...p, contactName: v }))}
              />
              <FormField
                name="contactPhone"
                label="Contact phone"
                value={form.contactPhone}
                onChange={(v) => setForm((p) => ({ ...p, contactPhone: v }))}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="submit" disabled={saving} style={btnPrimary}>
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </form>

          <div style={{ ...card, maxWidth: '560px' }}>
            <h3 style={{ marginTop: 0 }}>Organization details</h3>
            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem', margin: 0 }}>
              <dt style={{ color: colors.textMuted }}>Name</dt>
              <dd style={{ margin: 0 }}>{org.name}</dd>
              <dt style={{ color: colors.textMuted }}>Address</dt>
              <dd style={{ margin: 0 }}>{readOnlyAddress(org)}</dd>
              <dt style={{ color: colors.textMuted }}>CLIA number</dt>
              <dd style={{ margin: 0 }}>{org.cliaNumber ?? '—'}</dd>
              <dt style={{ color: colors.textMuted }}>Lab director</dt>
              <dd style={{ margin: 0 }}>{org.labDirector ?? '—'}</dd>
              <dt style={{ color: colors.textMuted }}>NPI</dt>
              <dd style={{ margin: 0 }}>{org.npi ?? '—'}</dd>
            </dl>
            <p style={{ marginTop: '1rem', marginBottom: 0, fontSize: '13px', color: colors.textMuted }}>
              To change your organization name, address, or compliance details (CLIA, NPI, lab director), contact{' '}
              <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
            </p>
          </div>
        </>
      )}
    </div>
  )
}
